import { useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Grid as Grid2, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { isVideoFile } from '@modules/_shared/components/PropertyFiles';
import {
  GET_LISTING_DETAILS,
  LISTING_DOCUMENTS,
} from '@modules/listings/lotsQueries';

import { FileDetailPanel } from './FileDetailPanel';
import { FilesSortableList } from './FilesSortableList';

export const ListingFilesMasterDetail = ({
  lotId,
  fileKind,
  readOnly = false,
  leftColumnHeader,
}: {
  lotId: string;
  fileKind: 'videos' | 'documents';
  readOnly?: boolean;
  leftColumnHeader?: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const selectedFileId = useParams()['*'] || undefined;
  const autoSelectedKind = useRef<typeof fileKind | null>(null);

  const { data, loading, refetch, error } = useQuery(LISTING_DOCUMENTS, {
    variables: { lotId },
  });

  const [queryError, setQueryError] = useState<ErrorLike | null>(null);

  useEffect(() => {
    setQueryError(error ?? null);
  }, [error]);

  const propertyId = data?.lots_by_pk?.property_id;

  const files = useMemo(() => {
    const all = data?.lots_by_pk?.property.property_files ?? [];
    return fileKind === 'videos'
      ? all.filter(item => isVideoFile(item.file))
      : all.filter(item => !isVideoFile(item.file));
  }, [data?.lots_by_pk?.property.property_files, fileKind]);

  const refetchQueries = useMemo(
    () => [
      { query: LISTING_DOCUMENTS, variables: { lotId } },
      { query: GET_LISTING_DETAILS, variables: { id: lotId } },
    ],
    [lotId],
  );

  useEffect(() => {
    if (
      autoSelectedKind.current !== fileKind &&
      !isMobile &&
      selectedFileId == null &&
      files.length > 0
    ) {
      navigate(`./${files[0].file.id}`, { replace: true });
      autoSelectedKind.current = fileKind;
    }
  }, [fileKind, files, isMobile, selectedFileId, navigate]);

  return (
    <Grid2 container sx={{ height: '100%' }}>
      <Grid2
        size={{ xs: 12, lg: 5 }}
        sx={theme => ({
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          borderRight: `1px solid ${(theme.vars ?? theme).palette.divider}`,
        })}
      >
        {leftColumnHeader}
        <FilesSortableList
          propertyId={propertyId}
          files={files}
          loading={loading}
          readOnly={readOnly}
          fileKind={fileKind}
          refetchQueries={refetchQueries}
          onDelete={async deletedId => {
            await refetch();
            if (deletedId === selectedFileId) {
              navigate('.', { replace: true });
            }
          }}
          onSelectFile={fileId => navigate(`./${fileId}`, { replace: true })}
          selectedFileId={selectedFileId}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, lg: 7 }} sx={{ position: 'relative' }}>
        {propertyId != null && (
          <FileDetailPanel
            propertyId={propertyId}
            files={files}
            selectedFileId={selectedFileId}
            readOnly={readOnly}
            refetchQueries={refetchQueries}
          />
        )}
      </Grid2>
      <MutationErrorSnackbar
        error={queryError}
        onClose={() => setQueryError(null)}
      />
    </Grid2>
  );
};
